import type { BatchDraftItem, BatchDraftInstance } from '../types/batch-entry';
import type {
  CreateItemFormValues,
  EditItemFormValues,
  InstanceFormValues,
  SelectedProduct,
} from '../types/item-form';

/**
 * Build a SelectedProduct from BatchDraftItem fields.
 * Returns null when the item has no product association (manual entry).
 */
function buildSelectedProduct(item: BatchDraftItem): SelectedProduct | null {
  if (item.productId) {
    return {
      type: 'local',
      product: {
        id: item.productId,
        name: item.displayName,
        brand: item.displayBrand || null,
        barcode: item.barcode || null,
        description: null,
        imageUrl: item.displayImageUrl || null,
        category: null,
        source: 'user',
        defaultQuantity: null,
        defaultUnit: null,
        names: null,
      },
    };
  }

  if (item.barcode && item.isExternal) {
    return {
      type: 'external',
      product: {
        barcode: item.barcode,
        name: item.displayName,
        brand: item.displayBrand || null,
        description: null,
        imageUrl: item.displayImageUrl || null,
        category: null,
        defaultQuantity: null,
        defaultUnit: null,
        names: null,
      },
    };
  }

  return null;
}

/**
 * Map a BatchDraftItem to CreateItemFormValues.
 * Used when the batch sheet is in "create-item" mode (unknown/manual items).
 */
export function batchDraftToCreateItemValues(item: BatchDraftItem): CreateItemFormValues {
  return {
    selectedProduct: buildSelectedProduct(item),
    customName: item.customName || item.displayName || '',
    barcode: item.barcode,
    unit: item.unit,
    storageLocationId: item.storageLocationId || null,
    quantity: item.quantity || 1,
    purchaseDate: item.purchaseDate,
    expiryDate: item.expiryDate,
    notes: item.notes,
  };
}

/**
 * Map a BatchDraftItem to EditItemFormValues.
 * Used when the batch sheet is in "edit-item" mode (editing the whole item).
 */
export function batchDraftToEditItemValues(item: BatchDraftItem): EditItemFormValues {
  return {
    selectedProduct: buildSelectedProduct(item),
    customName: item.customName || item.displayName || '',
    unit: item.unit,
    storageLocationId: item.storageLocationId || null,
    quantity: item.quantity || 1,
    notes: item.notes,
  };
}

/**
 * Map a BatchDraftItem + optional instance to InstanceFormValues.
 * Used for "edit-instance" and "create-instance" (add batch) modes.
 */
export function batchDraftToInstanceValues(
  item: BatchDraftItem,
  instanceId?: string
): InstanceFormValues {
  if (instanceId) {
    const instance = item.instances?.find((i) => i.instanceId === instanceId);
    if (instance) {
      return {
        quantity: instance.quantity,
        purchaseDate: instance.purchaseDate,
        expiryDate: instance.expiryDate,
        notes: instance.notes,
      };
    }
  }

  // Default values for adding a new batch
  return {
    quantity: 1,
    purchaseDate: undefined,
    expiryDate: undefined,
    notes: undefined,
  };
}

/**
 * Map CreateItemFormValues back to a partial BatchDraftItem update.
 * Handles product selection, custom name resolution, and field mapping.
 */
export function createItemValuesToPartialBatchDraft(
  values: CreateItemFormValues
): Partial<BatchDraftItem> {
  if (values.selectedProduct) {
    const product = values.selectedProduct.product;
    const originalName = product.name;
    const hasCustomName = values.customName && values.customName !== originalName;

    const sp = values.selectedProduct;
    return {
      productId: sp.type === 'local' ? sp.product.id : undefined,
      barcode: product.barcode || undefined,
      customName: hasCustomName ? values.customName : undefined,
      displayName: values.customName || originalName,
      displayBrand: product.brand || undefined,
      displayImageUrl: product.imageUrl || undefined,
      isExternal: sp.type === 'external',
      quantity: values.quantity,
      unit: values.unit,
      storageLocationId: values.storageLocationId || undefined,
      purchaseDate: values.purchaseDate,
      expiryDate: values.expiryDate,
      notes: values.notes,
    };
  }

  return {
    customName: values.customName || undefined,
    displayName: values.customName || 'Unknown Item',
    barcode: values.barcode,
    quantity: values.quantity,
    unit: values.unit,
    storageLocationId: values.storageLocationId || undefined,
    purchaseDate: values.purchaseDate,
    expiryDate: values.expiryDate,
    notes: values.notes,
  };
}

/**
 * Map EditItemFormValues back to a partial BatchDraftItem update.
 */
export function editItemValuesToPartialBatchDraft(
  values: EditItemFormValues,
  originalDisplayName?: string
): Partial<BatchDraftItem> {
  if (values.selectedProduct) {
    const product = values.selectedProduct.product;
    const originalName = product.name;
    const hasCustomName = values.customName && values.customName !== originalName;

    const sp = values.selectedProduct;
    return {
      productId: sp.type === 'local' ? sp.product.id : undefined,
      barcode: product.barcode || undefined,
      customName: hasCustomName ? values.customName : undefined,
      displayName: values.customName || originalName,
      displayBrand: product.brand || undefined,
      displayImageUrl: product.imageUrl || undefined,
      isExternal: sp.type === 'external',
      quantity: values.quantity,
      unit: values.unit,
      storageLocationId: values.storageLocationId || undefined,
      notes: values.notes,
    };
  }

  const hasCustomName = values.customName && values.customName !== originalDisplayName;

  return {
    customName: hasCustomName ? values.customName : undefined,
    displayName: values.customName || originalDisplayName || 'Unknown Item',
    quantity: values.quantity,
    unit: values.unit,
    storageLocationId: values.storageLocationId || undefined,
    notes: values.notes,
  };
}

/**
 * Map InstanceFormValues to a partial BatchDraftInstance (for instance updates).
 */
export function instanceValuesToPartialInstance(
  values: InstanceFormValues
): Partial<BatchDraftInstance> {
  return {
    quantity: values.quantity,
    purchaseDate: values.purchaseDate,
    expiryDate: values.expiryDate,
    notes: values.notes,
  };
}

/**
 * Map InstanceFormValues to a new batch instance (without instanceId).
 */
export function instanceValuesToNewBatch(
  values: InstanceFormValues
): Omit<BatchDraftInstance, 'instanceId'> {
  return {
    quantity: values.quantity,
    purchaseDate: values.purchaseDate,
    expiryDate: values.expiryDate,
    notes: values.notes,
    addedAt: Date.now(),
  };
}
